import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, TrendingDown, ExternalLink } from 'lucide-react';
import Modal from './ui/Modal';
import api from '../lib/api';
import { useAuthStore } from '../stores/auth.store';

// Auto-pops on the first authenticated page render after login, when the
// user's most recent scheduled, non-absent day fell below the configured
// under-target threshold. Per-user-per-date dismissed-flag in localStorage.
//
//   localStorage["wfm-undertarget-shown-<userId>-<YYYY-MM-DD>"] = '1'
//
// Pairs with target.missed notifications already pushed end-of-day — this
// popup is the "right now" surface, the notification is the persistent log.

function dismissKey(userId, date) {
  return `wfm-undertarget-shown-${userId}-${date}`;
}

export default function UnderTargetLoginPopup() {
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const location = useLocation();
  const navigate = useNavigate();
  const [anomaly, setAnomaly] = useState(null);
  const [open, setOpen] = useState(false);
  const lastCheckRef = useRef(0);

  const check = useCallback(async () => {
    if (!isAuthenticated || !user?.id) return;
    // De-dup checks within 1s to avoid double-firing on rapid re-renders.
    const now = Date.now();
    if (now - lastCheckRef.current < 1000) return;
    lastCheckRef.current = now;

    try {
      const res = await api.get('/analytics/me/last-day-anomaly');
      const a = res.data?.anomaly;
      if (!a) return;
      let dismissed = false;
      try { dismissed = localStorage.getItem(dismissKey(user.id, a.date)) === '1'; } catch (_) {}
      if (dismissed) return;
      setAnomaly(a);
      setOpen(true);
    } catch (_) {
      // silent — login popup is best-effort
    }
  }, [isAuthenticated, user?.id]);

  useEffect(() => { check(); }, [check, location.pathname]);

  const dismiss = () => {
    if (anomaly && user?.id) {
      try { localStorage.setItem(dismissKey(user.id, anomaly.date), '1'); } catch (_) {}
    }
    setOpen(false);
    setAnomaly(null);
  };

  const goToPratiche = () => {
    dismiss();
    navigate('/pratiche');
  };

  if (!open || !anomaly) return null;

  const dateDisplay = new Date(anomaly.date + 'T00:00:00Z').toLocaleDateString('it-IT', {
    weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
  });

  return (
    <Modal
      onClose={dismiss}
      size="lg"
      title={
        <span className="flex items-start gap-2">
          <AlertTriangle size={20} style={{ color: 'var(--wfm-warning)', marginTop: 2 }} />
          Target non raggiunto
        </span>
      }
    >
        <p className="wfm-small mb-3" style={{ color: 'var(--wfm-text-mid)' }}>
          Nell'ultimo giorno lavorativo, <strong>{dateDisplay}</strong>, hai chiuso sotto il target.
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
          <Stat label="Dichiarate" value={anomaly.dichiarate} color="var(--wfm-text-main)" />
          <Stat label="Target scalato" value={anomaly.target_scaled} color="var(--wfm-primary)" />
          <Stat label="Orario previsto" value={`${anomaly.scheduled_hours}h`} color="var(--wfm-text-mid)" />
          <Stat label="Raggiunto" value={`${anomaly.pct}%`} color={anomaly.pct < 50 ? 'var(--wfm-error)' : 'var(--wfm-warning)'} icon={<TrendingDown size={14} />} />
        </div>

        <p className="wfm-tiny" style={{ color: 'var(--wfm-text-light)' }}>
          Il target visualizzato è già scalato in base alle tue ore programmate per la giornata
          (su un giorno standard di {Math.round(anomaly.scheduled_hours > 0 ? (anomaly.target_scaled / anomaly.dichiarate || 1) : 8) /* hint */}).
        </p>

        <div className="flex gap-2 justify-end mt-4">
          <button onClick={dismiss} className="wfm-btn-secondary wfm-btn-sm">Ho capito</button>
          <button onClick={goToPratiche} className="wfm-btn-primary wfm-btn-sm">
            <ExternalLink size={14} /> Vai alle pratiche
          </button>
        </div>
    </Modal>
  );
}

function Stat({ label, value, color, icon }) {
  return (
    <div className="wfm-kpi" style={{ padding: '0.5rem' }}>
      <p className="wfm-tiny" style={{ color: 'var(--wfm-text-light)' }}>{label}</p>
      <p className="text-lg font-bold flex items-center gap-1" style={{ color }}>
        {icon}{value}
      </p>
    </div>
  );
}
